import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Card, CardBody, Container, Media, Spinner } from 'reactstrap';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import ArrowBack from '@material-ui/icons/ArrowBack';
import MaterialReactTable from 'material-react-table';
import moment from 'moment';
import SimpleCard from '../../../../utils/card/SimpleCard';
import Dropzone from 'react-dropzone';
import currencyFormatter from '../../../../utils/currencyFormatter';
import { getDocs, selectValues } from '../../../../utils/fc';
import qs from 'qs';
import oikosApi from '../../../../services/oikos';
import { getFileName, uploadFileToDocumento } from '../../../../utils/oikosUploadDocumento';
import { OIKOS_FILES_BASE, TP_DOC_NF_DANFE, TP_DOC_NF_XML } from '../../../../constants/oikosReceitas';
import { notifyError, notifySuccess } from '../../../../utils/notify';
import {
  canEmitNfse,
  DEFAULT_COD_TRIB_OPTIONS,
  emitNfse,
  fetchLastNfseModelByOrcRubr,
  getEmitMissingFields,
  getKaminoEmitStatus,
  kaminoStatusLabel,
  listarTabelasImposto,
} from '../../../../utils/kaminoNfse';
import { diffDays, fmtDateBr, inRange, isFaturado, isLiberado, monthRefToRange, num, parseDate, startOfDay, startOfToday } from './utils';
import SearchableSelect from 'react-select';
import CreatableSelect from 'react-select/creatable';

const KaminoStatusPill = ({ status }) => {
  const label = kaminoStatusLabel(status);
  const bg =
    status === 'sincronizado' ? 'rgba(86,100,210,0.14)' : status === 'aguardando' ? 'rgba(241,180,76,0.22)' : 'rgba(108,117,125,0.14)';
  const color = status === 'sincronizado' ? '#5664d2' : status === 'aguardando' ? '#b46900' : '#6c757d';
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 800,
        backgroundColor: bg,
        color,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
};

const summarizeFaturamento = (recs) => {
  const out = {
    totalCount: 0,
    faturadosCount: 0,
    liberadosCount: 0,
    bloqueadosCount: 0,
    valorTotal: 0,
    valorFaturados: 0,
    valorLiberados: 0,
    valorBloqueados: 0,
  };

  out.totalCount = Array.isArray(recs) ? recs.length : 0;
  if (!Array.isArray(recs) || recs.length === 0) return out;

  for (const r of recs) {
    const v = num(r?.rec_vlrBru);
    const fat = isFaturado(r);
    const lib = isLiberado(r);

    out.valorTotal += v;

    if (fat) {
      out.faturadosCount += 1;
      out.valorFaturados += v;
    } else if (lib) {
      out.liberadosCount += 1;
      out.valorLiberados += v;
    } else {
      out.bloqueadosCount += 1;
      out.valorBloqueados += v;
    }
  }

  return out;
};

const StatLinesFaturamento = ({ recs }) => {
  const s = useMemo(() => summarizeFaturamento(recs), [recs]);

  return (
    <div className="d-flex flex-column" style={{ gap: 6 }}>
      <div style={{ fontSize: 18, fontWeight: 800, lineHeight: 1.1 }}>{currencyFormatter.format(s.valorTotal)}</div>
      <div style={{ fontSize: 12, color: '#6c757d' }}>
        <span style={{ fontWeight: 700, color: '#4e515e' }}>{s.totalCount}</span> faturamentos
      </div>

      <div style={{ height: 6 }} />

      <div className="d-flex flex-column" style={{ gap: 2, fontSize: 12 }}>
        <div className="d-flex align-items-center" style={{ gap: 8 }}>
          <div style={{ width: 44, fontWeight: 800 }}>{s.faturadosCount}</div>
          <div style={{ flex: 1 }}>faturados</div>
          <div style={{ fontWeight: 800 }}>{currencyFormatter.format(s.valorFaturados)}</div>
        </div>
        <div className="d-flex align-items-center" style={{ gap: 8 }}>
          <div style={{ width: 44, fontWeight: 800 }}>{s.liberadosCount}</div>
          <div style={{ flex: 1 }}>liberados</div>
          <div style={{ fontWeight: 800 }}>{currencyFormatter.format(s.valorLiberados)}</div>
        </div>
        <div className="d-flex align-items-center" style={{ gap: 8 }}>
          <div style={{ width: 44, fontWeight: 800 }}>{s.bloqueadosCount}</div>
          <div style={{ flex: 1 }}>bloqueados</div>
          <div style={{ fontWeight: 800 }}>{currencyFormatter.format(s.valorBloqueados)}</div>
        </div>
      </div>
    </div>
  );
};

const MetricCard = ({ title, recs, onClick, className, selected }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      className={className}
      style={{ width: '100%' }}
      role="button"
      tabIndex={0}
      aria-pressed={!!selected}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (typeof onClick === 'function') onClick();
        }
      }}
    >
      <Card
        style={{
          transition: 'border 0.18s ease, box-shadow 0.18s ease',
          border: selected ? '2px solid #4e515e' : hovered ? '2px solid #505A62' : '2px solid transparent',
          boxShadow: selected ? '0 6px 20px rgba(0,0,0,0.10)' : undefined,
          cursor: 'pointer',
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <CardBody>
          <Media>
            <Media body className="overflow-hidden">
              <div className="d-flex justify-content-between align-items-center">
                <div style={{ fontWeight: 'bold' }} className="text-truncate font-size-14 mb-2">
                  {title}
                </div>
              </div>
              <StatLinesFaturamento recs={recs} />
            </Media>
          </Media>
        </CardBody>
      </Card>
    </div>
  );
};

const YesNoDot = ({ value, trueColor = '#1cbb8c', falseColor = '#ff5757' }) => {
  const backgroundColor = value ? trueColor : falseColor;
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ backgroundColor, borderRadius: '50%', width: '12px', height: '12px' }} />
    </div>
  );
};

const getSituacao = (rec, today) => {
  if (isFaturado(rec)) return 'faturado';
  const dtPrev = parseDate(rec?.rec_dtNfPrev);
  if (dtPrev && dtPrev < startOfDay(today)) return 'atrasado';
  if (isLiberado(rec)) return 'liberado';
  return 'bloqueado';
};

const situacaoLabel = (key) => {
  if (key === 'faturado') return 'Faturado';
  if (key === 'atrasado') return 'Atrasado';
  if (key === 'liberado') return 'Liberado';
  if (key === 'bloqueado') return 'Bloqueado';
  return key || '-';
};

const SituacaoPill = ({ value }) => {
  const bg =
    value === 'faturado'
      ? '#5664d2'
      : value === 'atrasado'
        ? '#ff5757'
        : value === 'liberado'
          ? '#1cbb8c'
          : '#4e515e';
  return (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 999, color: '#fff', backgroundColor: bg, whiteSpace: 'nowrap' }}>
        {situacaoLabel(value)}
      </span>
    </div>
  );
};

const OIKOS_NF_UP_ENDPOINT = 'https://oikos.ecoss.is/oikos/_fin.nf.up.php';

const extractNfNumFromXmlText = (text) => {
  if (!text) return null;
  const m1 = text.match(/<Numero>\s*([^<]+)\s*<\/Numero>/i);
  if (m1?.[1]) return String(m1[1]).trim();
  const m2 = text.match(/<nNFSe>\s*([^<]+)\s*<\/nNFSe>/i);
  if (m2?.[1]) return String(m2[1]).trim();
  return null;
};

const extractFirstTagText = (xml, tag) => {
  if (!xml || !tag) return null;
  const re = new RegExp(`<${tag}>([\\s\\S]*?)<\\/${tag}>`, 'i');
  const m = String(xml).match(re);
  if (!m?.[1]) return null;
  return String(m[1]).trim();
};

const guessDateFromXml = (xml) => {
  const candidates = ['Competencia', 'competencia', 'DataEmissao', 'dataEmissao', 'dhEmi', 'dCompet', 'dEmi'];
  for (const t of candidates) {
    const v = extractFirstTagText(xml, t);
    if (!v) continue;
    const m = v.match(/^(\d{4}-\d{2}-\d{2})/);
    if (m?.[1]) return m[1];
    const m2 = v.match(/^(\d{2}\/\d{2}\/\d{4})/);
    if (m2?.[1]) return m2[1];
  }
  return null;
};

const toCompetencia = (dateLike) => {
  const d = parseDate(dateLike);
  if (!d) return null;
  return moment(d).startOf('month').format('YYYY-MM-DD');
};

const ensureNfFromXml = async ({ xmlText, nfNum }) => {
  try {
    const existingRes = await oikosApi.get(
      `/?${qs.stringify({ action: 'get', table: 'nf', data: { nf_num: nfNum, nf_ativo: 1 } })}`
    );
    const existing = Array.isArray(existingRes?.data) ? existingRes.data[0] : null;
    if (existing?.nf_id) {
      // Garante que o conteúdo do XML fica salvo na coluna nf.nf_xml.
      await oikosApi.post('/', {
        action: 'update',
        table: 'nf',
        data: { nf_id: existing.nf_id, nf_xml: xmlText },
      });
      return { nf_id: existing.nf_id, nf_num: existing.nf_num, reused: true };
    }
  } catch (e) {}

  const dtGuess = guessDateFromXml(xmlText);
  const nf_comp = toCompetencia(dtGuess);
  const desc =
    extractFirstTagText(xmlText, 'Discriminacao') ||
    extractFirstTagText(xmlText, 'discriminacao') ||
    extractFirstTagText(xmlText, 'Descricao') ||
    extractFirstTagText(xmlText, 'descricao') ||
    null;

  const created = await oikosApi.post('/', {
    action: 'insert',
    table: 'nf',
    data: { nf_num: nfNum, nf_comp, nf_desc: desc, nf_status: 'importada', nf_ativo: 1, nf_xml: xmlText },
  });
  const row = Array.isArray(created?.data) ? created.data[0] : null;
  const nf_id = row?.nf_id;
  if (!nf_id) throw new Error('Falha ao criar NF (nf_id não retornado).');
  return { nf_id, nf_num: nfNum, reused: false, nf_comp };
};

const DetailsCard = ({ rec, onRecPatched, onNeedReload }) => {
  const { user } = useSelector((state) => state.auth);
  const canEditEmit = canEmitNfse(user);

  const [nfModalOpen, setNfModalOpen] = useState(false);
  const [nfLoading, setNfLoading] = useState(false);
  const [nfError, setNfError] = useState(null);
  const [nf, setNf] = useState(null);

  const [nfId, setNfId] = useState(rec?.nf_id ?? null);
  const [recFatLocal, setRecFatLocal] = useState(rec?.rec_fat ?? null);
  const [kaminoIdLocal, setKaminoIdLocal] = useState(rec?.kamino_id ?? null);

  const [docsLoading, setDocsLoading] = useState(false);
  const [docsError, setDocsError] = useState(null);
  const [docs, setDocs] = useState([]);

  const [uploadingXml, setUploadingXml] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);

  const [nfDescricao, setNfDescricao] = useState(rec?.rec_NFDescricao || '');
  const [kaminoImposto, setKaminoImposto] = useState(rec?.kamino_imposto != null && rec?.kamino_imposto !== '' ? String(rec.kamino_imposto) : '');
  const [codTrib, setCodTrib] = useState(rec?.contrato_codTrib || '');
  const [pjTomadorId, setPjTomadorId] = useState(rec?.pj_tomador ?? null);
  const [pjTomadorNomeLocal, setPjTomadorNomeLocal] = useState(rec?.pjTomadorNome || '');
  const [pjTomadorKaminoLocal, setPjTomadorKaminoLocal] = useState(rec?.pjTomadorKaminoId ?? null);
  const [munId, setMunId] = useState(rec?.mun_id ?? null);
  const [ufIBGECod, setUfIBGECod] = useState(null);
  const [savingNfFields, setSavingNfFields] = useState(false);
  const [emitting, setEmitting] = useState(false);
  const [loadingModelo, setLoadingModelo] = useState(false);

  const [impostoOptions, setImpostoOptions] = useState([]);
  const [pjOptions, setPjOptions] = useState([]);
  const [ufOptions, setUfOptions] = useState([]);
  const [munOptionsAll, setMunOptionsAll] = useState([]);
  const [codTribOptions, setCodTribOptions] = useState(DEFAULT_COD_TRIB_OPTIONS);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [impostoLoadError, setImpostoLoadError] = useState(null);

  const closeNfModal = () => setNfModalOpen(false);
  const openNfModal = () => setNfModalOpen(true);

  const recNfId = rec?.nf_id ?? null;
  const recFat = rec?.rec_fat ?? null;
  const recKaminoId = rec?.kamino_id ?? null;

  useEffect(() => {
    let cancelled = false;
    const fetchNf = async () => {
      if (!recNfId) return;
      setNfLoading(true);
      setNfError(null);
      try {
        const res = await oikosApi.get(`/?${qs.stringify({ action: 'get', table: 'nf', data: { nf_id: recNfId, nf_ativo: 1 } })}`);
        if (cancelled) return;
        const row = Array.isArray(res?.data) ? res.data[0] : null;
        if (!row) {
          setNf(null);
          setNfError(`NF não encontrada para nf_id=${recNfId}.`);
          return;
        }
        setNf(row);
      } catch (e) {
        if (cancelled) return;
        setNf(null);
        setNfError(e?.message || 'Erro ao buscar a Nota Fiscal.');
      } finally {
        if (!cancelled) setNfLoading(false);
      }
    };
    fetchNf();
    return () => {
      cancelled = true;
    };
  }, [recNfId]);

  useEffect(() => {
    setNfId(recNfId);
    setRecFatLocal(recFat);
    setKaminoIdLocal(recKaminoId);
    setNfDescricao(rec?.rec_NFDescricao || '');
    setKaminoImposto(rec?.kamino_imposto != null && rec?.kamino_imposto !== '' ? String(rec.kamino_imposto) : '');
    setCodTrib(rec?.contrato_codTrib || '');
    setPjTomadorId(rec?.pj_tomador ?? null);
    setPjTomadorNomeLocal(rec?.pjTomadorNome || '');
    setPjTomadorKaminoLocal(rec?.pjTomadorKaminoId ?? null);
    setMunId(rec?.mun_id ?? null);
  }, [
    recNfId,
    recFat,
    recKaminoId,
    rec?.rec_NFDescricao,
    rec?.kamino_imposto,
    rec?.contrato_codTrib,
    rec?.pj_tomador,
    rec?.pjTomadorNome,
    rec?.pjTomadorKaminoId,
    rec?.mun_id,
  ]);

  useEffect(() => {
    if (!canEditEmit) return undefined;
    let cancelled = false;
    const mapPj = (p) => {
      const nome = p.pj_razaosocial || p.pj_nomefanta || `PJ ${p.pj_id}`;
      const kid = p.kamino_id;
      const hasKid = kid != null && String(kid).trim() !== '' && Number(kid) !== 0;
      return {
        value: p.pj_id,
        label: hasKid ? nome : `${nome} (sem ID Kamino)`,
        kamino_id: hasKid ? kid : null,
        mun_id: p.mun_id ?? null,
        nome,
      };
    };
    const loadOptions = async () => {
      setOptionsLoading(true);
      setImpostoLoadError(null);
      try {
        const [impostosRes, muns, ufs, pjsRes] = await Promise.all([
          listarTabelasImposto()
            .then((list) => ({ ok: true, list }))
            .catch((e) => {
              console.warn('[DetailsCard] listarTabelasImposto:', e?.message || e);
              return { ok: false, list: [], error: e?.message || 'Falha ao carregar tabelas de imposto.' };
            }),
          selectValues('mun', 'mun_id', 'mun_nome', { mun_ativo: 1 }, { uf_IBGECod: 'uf_IBGECod' }).catch(() => []),
          selectValues('uf', 'uf_id', 'uf_nome', {}, { uf_IBGECod: 'uf_IBGECod', uf_sigla: 'uf_sigla' }).catch(() => []),
          oikosApi
            .get(
              `/?${qs.stringify({
                action: 'get',
                table: 'pj',
                data: { pj_ativo: 1 },
              })}`
            )
            .catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;

        if (!impostosRes.ok) setImpostoLoadError(impostosRes.error);
        setImpostoOptions(Array.isArray(impostosRes.list) ? impostosRes.list : []);
        setMunOptionsAll(Array.isArray(muns) ? muns : []);
        setUfOptions(
          (Array.isArray(ufs) ? ufs : []).map((u) => ({
            ...u,
            label: u.uf_sigla ? `${u.uf_sigla} — ${u.label}` : u.label,
          })),
        );

        const pjRows = Array.isArray(pjsRes?.data) ? pjsRes.data : [];
        const mapped = pjRows
          .map(mapPj)
          .sort((a, b) => String(a.label).localeCompare(String(b.label), 'pt-BR'));
        if (cancelled) return;
        setPjOptions(mapped);

        // Se esta receita já tiver tomador salvo, mantém a seleção (não inventa origem).
        const tomadorId = Number(rec?.pj_tomador) || 0;
        if (tomadorId > 0) {
          let opt = mapped.find((o) => Number(o.value) === tomadorId);
          if (!opt) {
            try {
              const one = await oikosApi.get(
                `/?${qs.stringify({
                  action: 'get',
                  table: 'pj',
                  data: { pj_id: tomadorId },
                })}`
              );
              const row = Array.isArray(one?.data) ? one.data[0] : null;
              if (row) {
                opt = mapPj(row);
                setPjOptions((prev) => [opt, ...prev.filter((o) => Number(o.value) !== tomadorId)]);
              }
            } catch (e) {
              console.warn('[DetailsCard] load tomador salvo:', e?.message || e);
            }
          }
          if (opt) {
            setPjTomadorId(tomadorId);
            setPjTomadorNomeLocal(opt.nome || rec?.pjTomadorNome || '');
            setPjTomadorKaminoLocal(opt.kamino_id ?? rec?.pjTomadorKaminoId ?? null);
          }
        } else {
          setPjTomadorId(null);
          setPjTomadorNomeLocal('');
          setPjTomadorKaminoLocal(null);
        }

        const tribSet = new Map(DEFAULT_COD_TRIB_OPTIONS.map((o) => [String(o.value), o]));
        if (rec?.contrato_codTrib) {
          const v = String(rec.contrato_codTrib);
          if (!tribSet.has(v)) tribSet.set(v, { value: v, label: v });
        }
        setCodTribOptions(Array.from(tribSet.values()));

        // UF a partir do município já salvo nesta receita (se houver)
        const initialMunId = Number(rec?.mun_id) || 0;
        if (initialMunId > 0) {
          const munOpt = (Array.isArray(muns) ? muns : []).find((m) => Number(m.value) === initialMunId);
          if (munOpt?.uf_IBGECod != null) setUfIBGECod(munOpt.uf_IBGECod);
        }
      } finally {
        if (!cancelled) setOptionsLoading(false);
      }
    };
    loadOptions();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEditEmit, rec?.rec_id]);

  const refreshDocs = async (id) => {
    if (!id) {
      setDocs([]);
      return;
    }
    setDocsLoading(true);
    setDocsError(null);
    try {
      const res = await getDocs('nf', id);
      setDocs(Array.isArray(res) ? res : []);
    } catch (e) {
      setDocs([]);
      setDocsError(e?.message || 'Erro ao buscar anexos da NF.');
    } finally {
      setDocsLoading(false);
    }
  };

  useEffect(() => {
    refreshDocs(nfId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nfId]);

  const LabelValue = ({ label, value, grow }) => (
    <div style={{ display: 'flex', gap: 6, flex: grow ? 1 : undefined, minWidth: 0 }}>
      <span style={{ color: '#6c757d', whiteSpace: 'nowrap' }}>{label}:</span>
      <span style={{ fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value ?? '-'}</span>
    </div>
  );

  const formatDateTime = (v) => (v ? moment(v).format('DD/MM/YYYY HH:mm') : '-');
  const formatDateOnly = (v) => (v ? fmtDateBr(v) : '-');
  const formatMoney = (v) => currencyFormatter.format(num(v));
  const formatPercent = (v) => `${new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num(v))}%`;

  if (!rec) return null;

  const recFatShown = recFatLocal ?? rec.rec_fat;
  const dtPrev = parseDate(rec?.rec_dtNfPrev);
  const today = startOfToday();
  const diasParaLimite = dtPrev ? diffDays(dtPrev, today) : null;
  const situacao = getSituacao(rec, today);

  const kaminoStatus = getKaminoEmitStatus({
    ...rec,
    kamino_id: kaminoIdLocal ?? rec.kamino_id,
    nf_id: nfId ?? rec.nf_id,
    rec_fat: recFatShown,
  });
  const emitDraft = {
    ...rec,
    rec_NFDescricao: nfDescricao,
    kamino_imposto: kaminoImposto,
    contrato_codTrib: codTrib,
    kamino_id: kaminoIdLocal,
    nf_id: nfId,
    rec_fat: recFatShown,
    pj_tomador: pjTomadorId,
    mun_id: munId,
    pjTomadorKaminoId: pjTomadorKaminoLocal ?? rec.pjTomadorKaminoId,
    pjPrestadorKaminoId: rec.pjPrestadorKaminoId,
    pj_prestador: rec.pjPrestadorId || rec.pj_prestador,
  };
  const prestadorIdResolved = Number(rec.pjPrestadorId || rec.pj_prestador) || null;
  const prestadorNomeResolved = rec.pjPrestadorNome || '-';
  const prestadorKaminoOk = Boolean(rec.pjPrestadorKaminoId);
  const prestadorInferidoDoContrato =
    prestadorIdResolved > 0 && !(Number(rec.pj_prestador) > 0);
  const emitMissing = getEmitMissingFields(emitDraft);
  const canShowEmitForm = canEditEmit && isLiberado(rec) && Number(recFatShown) !== 1;
  const canClickEmit = canShowEmitForm && kaminoStatus === 'nao_enviado' && emitMissing.length === 0 && !emitting && !savingNfFields;
  const munOptionsFiltered = ufIBGECod != null && ufIBGECod !== ''
    ? munOptionsAll.filter((m) => String(m.uf_IBGECod) === String(ufIBGECod))
    : [];
  const selectedUf =
    ufOptions.find((u) => String(u.uf_IBGECod) === String(ufIBGECod)) || null;
  const selectedTomador =
    pjOptions.find((o) => Number(o.value) === Number(pjTomadorId)) ||
    (pjTomadorId
      ? {
          value: pjTomadorId,
          label: pjTomadorNomeLocal || rec?.pjTomadorNome || `PJ ${pjTomadorId}`,
          kamino_id: pjTomadorKaminoLocal,
          nome: pjTomadorNomeLocal || rec?.pjTomadorNome || '',
        }
      : null);

  const getLatestDocByTp = (tpId) => {
    const filtered = (docs || []).filter((d) => Number(d?.tpDocumento_id) === Number(tpId));
    if (filtered.length === 0) return null;
    filtered.sort((a, b) => Number(b?.arquivo_id || 0) - Number(a?.arquivo_id || 0));
    return filtered[0];
  };

  const xmlDoc = getLatestDocByTp(TP_DOC_NF_XML);
  const pdfDoc = getLatestDocByTp(TP_DOC_NF_DANFE);

  const openDoc = (doc) => {
    const path = doc?.arquivo_caminho;
    if (!path) return;
    window.open(`${OIKOS_FILES_BASE}${path}`, '_blank');
  };

  const handleUploadXml = async (acceptedFiles) => {
    const file = acceptedFiles?.[0];
    if (!file) return;
    if (!rec?.rec_id) {
      notifyError('rec_id não encontrado.');
      return;
    }
    setUploadingXml(true);
    try {
      const xmlText = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ''));
        reader.onerror = () => reject(new Error('Falha ao ler XML.'));
        reader.readAsText(file);
      });

      const nfNum = extractNfNumFromXmlText(xmlText);
      if (!nfNum) throw new Error('Não consegui identificar o número da NF no XML.');

      if (nfId) {
        // NF já existe: atualiza o XML (nf_xml) também.
        await oikosApi.post('/', {
          action: 'update',
          table: 'nf',
          data: { nf_id: nfId, nf_xml: xmlText },
        });
        await uploadFileToDocumento({ tpDocumentoId: TP_DOC_NF_XML, tabRef: 'nf', tabId: nfId, file, documentoDesc: `XML NF ${nfNum}` });
        notifySuccess('XML anexado com sucesso na NF vinculada.');
        await refreshDocs(nfId);
        if (onNeedReload) onNeedReload();
        return;
      }

      const fd = new FormData();
      fd.append('arquivos', file, getFileName(file));
      let newNfId = null;
      let nfNumFromApi = null;
      let nfCompFromApi = null;

      try {
        const upRes = await fetch(OIKOS_NF_UP_ENDPOINT, { method: 'POST', body: fd, credentials: 'include' });
        if (!upRes.ok) {
          const t = await upRes.text().catch(() => '');
          throw new Error(`Falha ao enviar XML para processamento (${upRes.status}). ${t}`);
        }
        const payload = await upRes.json().catch(() => null);
        if (!payload || payload.ok !== true) throw new Error(payload?.message || 'O endpoint de processamento não retornou ok=true.');
        newNfId = payload?.nf_id ?? null;
        nfNumFromApi = payload?.nf_num ?? null;
        if (!newNfId) throw new Error('nf_id não retornado pelo endpoint.');
      } catch (e) {
        const ensured = await ensureNfFromXml({ xmlText, nfNum });
        newNfId = ensured.nf_id;
        nfNumFromApi = ensured.nf_num;
        nfCompFromApi = ensured.nf_comp ?? null;
      }

      const nfInfoRes = await oikosApi.get(
        `/?${qs.stringify({ action: 'get', table: 'nf', data: { nf_id: newNfId, nf_ativo: 1 } })}`
      );
      const nfInfo = Array.isArray(nfInfoRes?.data) ? nfInfoRes.data[0] : null;
      const nfComp = nfInfo?.nf_comp || nfCompFromApi || null;

      await oikosApi.post('/', {
        action: 'update',
        table: 'rec',
        data: { rec_id: rec.rec_id, nf_id: newNfId, rec_fat: 1, ...(nfComp ? { rec_dtNfPrev: nfComp } : {}) },
      });

      // Garante que o conteúdo do XML fique salvo em nf.nf_xml.
      await oikosApi.post('/', {
        action: 'update',
        table: 'nf',
        data: { nf_id: newNfId, nf_xml: xmlText },
      });

      setNfId(newNfId);
      setRecFatLocal(1);
      if (typeof onRecPatched === 'function') {
        onRecPatched({ nf_id: newNfId, rec_fat: 1, ...(nfComp ? { rec_dtNfPrev: nfComp } : {}) });
      }

      await uploadFileToDocumento({ tpDocumentoId: TP_DOC_NF_XML, tabRef: 'nf', tabId: newNfId, file, documentoDesc: `XML NF ${nfNumFromApi || nfNum}` });
      notifySuccess('XML processado/anexado com sucesso.');
      await refreshDocs(newNfId);
      if (onNeedReload) onNeedReload();
    } catch (e) {
      notifyError(e?.message || 'Falha ao anexar XML.');
    } finally {
      setUploadingXml(false);
    }
  };

  const handleUploadPdf = async (acceptedFiles) => {
    const file = acceptedFiles?.[0];
    if (!file) return;
    if (!nfId) {
      notifyError('Envie o XML primeiro (nf_id ainda não definido).');
      return;
    }
    if (!xmlDoc?.arquivo_caminho) {
      notifyError('Envie/anexe o XML primeiro.');
      return;
    }

    setUploadingPdf(true);
    try {
      await uploadFileToDocumento({ tpDocumentoId: TP_DOC_NF_DANFE, tabRef: 'nf', tabId: nfId, file, documentoDesc: `DANFE ${getFileName(file)}` });
      notifySuccess('PDF (DANFE) anexado com sucesso.');
      await refreshDocs(nfId);
      if (onNeedReload) onNeedReload();
    } catch (e) {
      notifyError(e?.message || 'Falha ao anexar PDF.');
    } finally {
      setUploadingPdf(false);
    }
  };

  const handleSaveNfFields = async () => {
    if (!rec?.rec_id) {
      notifyError('rec_id não encontrado.');
      throw new Error('rec_id não encontrado.');
    }
    setSavingNfFields(true);
    try {
      const patch = {
        rec_id: rec.rec_id,
        rec_NFDescricao: String(nfDescricao || '').trim(),
        kamino_imposto: String(kaminoImposto || '').trim(),
        contrato_codTrib: String(codTrib || '').trim(),
        pj_tomador: pjTomadorId || null,
        mun_id: munId || null,
        // Persiste prestador inferido do contrato (contrato_contratada) para o emit PHP
        pj_prestador: Number(rec.pjPrestadorId || rec.pj_prestador) || null,
      };
      await oikosApi.post('/', { action: 'update', table: 'rec', data: patch });
      if (typeof onRecPatched === 'function') {
        onRecPatched({
          rec_NFDescricao: patch.rec_NFDescricao,
          kamino_imposto: patch.kamino_imposto,
          contrato_codTrib: patch.contrato_codTrib,
          pj_tomador: patch.pj_tomador,
          mun_id: patch.mun_id,
          pj_prestador: patch.pj_prestador,
          pjPrestadorId: patch.pj_prestador,
          pjTomadorNome: pjTomadorNomeLocal,
          pjTomadorKaminoId: pjTomadorKaminoLocal,
          pjPrestadorNome: rec.pjPrestadorNome,
          pjPrestadorKaminoId: rec.pjPrestadorKaminoId,
        });
      }
      notifySuccess('Dados da NF salvos.');
      return patch;
    } catch (e) {
      notifyError(e?.message || 'Falha ao salvar dados da NF.');
      throw e;
    } finally {
      setSavingNfFields(false);
    }
  };

  const applyModeloFromRec = (model) => {
    if (!model) return;
    if (model.pj_tomador) {
      const tid = Number(model.pj_tomador);
      setPjTomadorId(tid);
      setPjTomadorNomeLocal(model.pjTomadorNome || '');
      setPjTomadorKaminoLocal(model.pjTomadorKaminoId ?? null);
      const opt = pjOptions.find((o) => Number(o.value) === tid);
      if (!opt && model.pjTomadorNome) {
        setPjOptions((prev) => [
          {
            value: tid,
            label: model.pjTomadorKaminoId
              ? model.pjTomadorNome
              : `${model.pjTomadorNome} (sem ID Kamino)`,
            kamino_id: model.pjTomadorKaminoId ?? null,
            mun_id: model.mun_id || model.pjTomadorMunId || null,
            nome: model.pjTomadorNome,
          },
          ...prev.filter((o) => Number(o.value) !== tid),
        ]);
      }
    }
    if (model.rec_NFDescricao != null) setNfDescricao(String(model.rec_NFDescricao || ''));
    if (model.kamino_imposto != null && String(model.kamino_imposto).trim() !== '') {
      setKaminoImposto(String(model.kamino_imposto));
    }
    if (model.contrato_codTrib) {
      const v = String(model.contrato_codTrib);
      setCodTrib(v);
      setCodTribOptions((prev) =>
        prev.some((o) => String(o.value) === v) ? prev : [...prev, { value: v, label: v }],
      );
    }
    const nextMun = Number(model.mun_id) || Number(model.pjTomadorMunId) || null;
    if (nextMun) {
      setMunId(nextMun);
      const munOpt = munOptionsAll.find((m) => Number(m.value) === nextMun);
      const ufCod = munOpt?.uf_IBGECod ?? model.munUfIBGECod ?? null;
      if (ufCod != null) setUfIBGECod(ufCod);
    } else if (model.munUfIBGECod != null) {
      setUfIBGECod(model.munUfIBGECod);
    }
  };

  const handleCarregarModelo = async () => {
    if (!(Number(rec?.orcRubr_id) > 0)) {
      notifyError('Receita sem vínculo de projeto (orcRubr) para carregar modelo.');
      return;
    }
    setLoadingModelo(true);
    try {
      const model = await fetchLastNfseModelByOrcRubr(rec.orcRubr_id, rec.rec_id);
      if (!model) {
        notifyError('Não há receita emitida anterior neste projeto para usar como modelo.');
        return;
      }
      applyModeloFromRec(model);
      notifySuccess(`Modelo carregado da receita #${model.rec_id}.`);
    } catch (e) {
      notifyError(e?.message || 'Falha ao carregar modelo.');
    } finally {
      setLoadingModelo(false);
    }
  };

  const handleEmitKamino = async () => {
    if (!rec?.rec_id) {
      notifyError('rec_id não encontrado.');
      return;
    }
    const draftRec = {
      ...rec,
      rec_NFDescricao: nfDescricao,
      kamino_imposto: kaminoImposto,
      contrato_codTrib: codTrib,
      kamino_id: kaminoIdLocal,
      nf_id: nfId,
      rec_fat: recFatLocal ?? rec.rec_fat,
      pj_tomador: pjTomadorId,
      mun_id: munId,
      pjTomadorKaminoId: pjTomadorKaminoLocal ?? rec.pjTomadorKaminoId,
      pjPrestadorKaminoId: rec.pjPrestadorKaminoId,
      pj_prestador: rec.pjPrestadorId || rec.pj_prestador,
    };
    const missing = getEmitMissingFields(draftRec);
    if (missing.length) {
      notifyError(`Não é possível emitir: ${missing.join('; ')}.`);
      return;
    }
    if (!window.confirm('Enviar esta receita à Kamino para emissão da NFS-e?')) return;

    setEmitting(true);
    try {
      const needsPrestadorPersist =
        Number(rec.pjPrestadorId || 0) > 0 &&
        Number(rec.pj_prestador || 0) !== Number(rec.pjPrestadorId || 0);
      const dirty =
        needsPrestadorPersist ||
        String(nfDescricao || '').trim() !== String(rec?.rec_NFDescricao || '').trim() ||
        String(kaminoImposto ?? '') !== String(rec?.kamino_imposto ?? '') ||
        String(codTrib || '').trim() !== String(rec?.contrato_codTrib || '').trim() ||
        Number(pjTomadorId || 0) !== Number(rec?.pj_tomador || 0) ||
        Number(munId || 0) !== Number(rec?.mun_id || 0);
      if (dirty) {
        try {
          await handleSaveNfFields();
        } catch {
          return;
        }
      }
      const payload = await emitNfse({ rec_id: rec.rec_id });
      const kid = payload?.kamino_id ?? null;
      setKaminoIdLocal(kid);
      if (typeof onRecPatched === 'function') onRecPatched({ kamino_id: kid });
      notifySuccess(payload?.message || 'Pedido enviado à Kamino.');
      if (onNeedReload) onNeedReload();
    } catch (e) {
      notifyError(e?.message || 'Falha ao enviar pedido à Kamino.');
    } finally {
      setEmitting(false);
    }
  };

  const dropStyle = {
    cursor: 'pointer',
    borderWidth: 2,
    borderRadius: 6,
    borderColor: '#ccc',
    borderStyle: 'dashed',
    backgroundColor: '#f8f8f8',
    outline: 'none',
    transition: 'all .2s ease-in-out',
    textAlign: 'center',
    padding: '10px 12px',
    fontSize: 12,
    color: '#666',
  };

  const compactDropStyle = { ...dropStyle, padding: '6px 10px', fontSize: 11, width: 210 };

  return (
    <div className="ml-4" style={{ width: '100%' }}>
      <SimpleCard
        standard={false}
        title={<h4 className="text-truncate font-size-14 mb-2">{`Detalhes: ${rec.rec_ep || 'EP'}`}</h4>}
        text={
          <div className="d-flex flex-column" style={{ gap: 10, fontSize: 13 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ color: '#6c757d', fontSize: 12 }}>Projeto</div>
                  <div style={{ fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {rec.recProjeto || '-'}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <SituacaoPill value={situacao} />
                  {diasParaLimite != null && !isFaturado(rec) && (
                    <span style={{ fontSize: 12, fontWeight: 800, color: diasParaLimite < 0 ? '#ff5757' : '#6c757d' }}>
                      {`${diasParaLimite}d`}
                    </span>
                  )}
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <LabelValue label="Bruto" value={currencyFormatter.format(num(rec.rec_vlrBru))} />
                <LabelValue label="Líquido" value={currencyFormatter.format(num(rec.rec_vlrLiq))} />
                <LabelValue label="Limite" value={fmtDateBr(rec.rec_dtNfPrev)} />
              </div>

              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <LabelValue label="Liberado" value={isLiberado(rec) ? 'Sim' : 'Não'} />
                <LabelValue label="Faturado" value={Number(recFatShown) === 1 ? 'Sim' : 'Não'} />
                <LabelValue label="Risco" value={rec.rec_risco ?? '-'} />
              </div>

              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <LabelValue label="Quit. ini" value={fmtDateBr(rec.rec_dtQuitIni)} />
                <LabelValue label="Quit. atual" value={fmtDateBr(rec.rec_dtQuitAtual)} />
                <LabelValue label="rec_id" value={rec.rec_id} />
              </div>
            </div>

            <hr className="my-2" />

            <div className="d-flex flex-column" style={{ gap: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 260 }}>
                  <div style={{ fontWeight: 800 }}>Nota Fiscal</div>
                  <div style={{ fontWeight: 900, color: '#4e515e' }}>{nf?.nf_num || (nfLoading ? 'Carregando...' : '-')}</div>
                  {nfId != null && (
                    <IconButton size="small" title="Visualizar Nota Fiscal" onClick={openNfModal}>
                      <i className="ri-file-search-line font-size-18" />
                    </IconButton>
                  )}
                </div>
                <KaminoStatusPill status={kaminoStatus} />
              </div>

              <div
                style={{
                  border: '1px solid rgba(78,81,94,0.18)',
                  borderRadius: 6,
                  padding: 10,
                  background: 'rgba(78,81,94,0.03)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                }}
              >
                <div style={{ fontWeight: 800, fontSize: 13 }}>Emissão Kamino</div>
                {kaminoStatus === 'aguardando' && (
                  <div style={{ fontSize: 12, color: '#b46900' }}>
                    Pedido enviado (ID {kaminoIdLocal || rec.kamino_id}). Liberar a emissão na plataforma Kamino; ao
                    recarregar esta tela o Oikos busca XML/PDF automaticamente.
                  </div>
                )}
                {kaminoStatus === 'sincronizado' && (
                  <div style={{ fontSize: 12, color: '#5664d2' }}>NFS-e sincronizada no Oikos.</div>
                )}

                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', fontSize: 12 }}>
                  {!canShowEmitForm && (
                    <span>
                      Tomador:{' '}
                      <strong>{pjTomadorNomeLocal || rec.pjTomadorNome || '-'}</strong>
                      {(pjTomadorKaminoLocal ?? rec.pjTomadorKaminoId) ? (
                        <span style={{ color: '#1cbb8c', marginLeft: 6 }}>OK</span>
                      ) : (
                        <span style={{ color: '#dc3545', marginLeft: 6 }}>sem ID Kamino</span>
                      )}
                    </span>
                  )}
                  <span>
                    Prestador:{' '}
                    <strong>{prestadorNomeResolved}</strong>
                    {prestadorInferidoDoContrato && (
                      <span style={{ color: '#6c757d', marginLeft: 6 }}>(contrato)</span>
                    )}
                    {prestadorKaminoOk ? (
                      <span style={{ color: '#1cbb8c', marginLeft: 6 }}>OK</span>
                    ) : (
                      <span style={{ color: '#dc3545', marginLeft: 6 }}>sem ID Kamino</span>
                    )}
                  </span>
                </div>

                {canShowEmitForm && (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={loadingModelo || savingNfFields || emitting || optionsLoading}
                        onClick={() => {
                          handleCarregarModelo().catch(() => {});
                        }}
                      >
                        {loadingModelo ? 'Carregando modelo...' : 'Carregar modelo'}
                      </Button>
                    </div>

                    <label style={{ fontSize: 12, color: '#6c757d', marginBottom: 0, width: '100%' }}>
                      Tomador do serviço
                      <div style={{ marginTop: 4 }}>
                        <SearchableSelect
                          options={pjOptions}
                          isLoading={optionsLoading}
                          isClearable
                          placeholder="Selecione o tomador..."
                          value={selectedTomador}
                          onChange={(opt) => {
                            setPjTomadorId(opt?.value ?? null);
                            setPjTomadorNomeLocal(opt?.nome || opt?.label || '');
                            setPjTomadorKaminoLocal(opt?.kamino_id ?? null);
                            if (opt?.mun_id) {
                              setMunId(opt.mun_id);
                              const munOpt = munOptionsAll.find((m) => Number(m.value) === Number(opt.mun_id));
                              if (munOpt?.uf_IBGECod != null) setUfIBGECod(munOpt.uf_IBGECod);
                            }
                          }}
                          styles={{ container: (base) => ({ ...base, fontSize: 12 }) }}
                        />
                      </div>
                      {pjTomadorId && (
                        <div style={{ marginTop: 4, fontSize: 11, color: pjTomadorKaminoLocal ? '#1cbb8c' : '#dc3545' }}>
                          {pjTomadorKaminoLocal ? `ID Kamino: ${pjTomadorKaminoLocal}` : 'Tomador sem ID Kamino'}
                        </div>
                      )}
                    </label>

                    <label style={{ fontSize: 12, color: '#6c757d', marginBottom: 0, width: '100%' }}>
                      Descrição da NF
                      <textarea
                        value={nfDescricao}
                        onChange={(e) => setNfDescricao(e.target.value)}
                        rows={3}
                        style={{
                          display: 'block',
                          width: '100%',
                          marginTop: 4,
                          fontSize: 12,
                          padding: 8,
                          borderRadius: 4,
                          border: '1px solid #ced4da',
                        }}
                      />
                    </label>

                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      <label style={{ fontSize: 12, color: '#6c757d', flex: 1, minWidth: 180, marginBottom: 0 }}>
                        Tabela imposto
                        <div style={{ marginTop: 4 }}>
                          <SearchableSelect
                            options={impostoOptions}
                            isLoading={optionsLoading}
                            isClearable
                            placeholder={impostoLoadError ? 'Erro ao carregar' : 'Selecione...'}
                            value={impostoOptions.find((o) => String(o.value) === String(kaminoImposto)) || null}
                            onChange={(opt) => setKaminoImposto(opt ? String(opt.value) : '')}
                            styles={{ container: (base) => ({ ...base, fontSize: 12 }) }}
                          />
                        </div>
                        {impostoLoadError && (
                          <div style={{ marginTop: 4, fontSize: 11, color: '#dc3545' }}>{impostoLoadError}</div>
                        )}
                      </label>
                      <label style={{ fontSize: 12, color: '#6c757d', flex: 1, minWidth: 160, marginBottom: 0 }}>
                        Código de serviço
                        <div style={{ marginTop: 4 }}>
                          <CreatableSelect
                            options={codTribOptions}
                            isClearable
                            placeholder="Selecione ou digite..."
                            formatCreateLabel={(input) => `Usar código "${input}"`}
                            value={
                              codTrib
                                ? codTribOptions.find((o) => String(o.value) === String(codTrib)) || {
                                    value: String(codTrib),
                                    label: String(codTrib),
                                  }
                                : null
                            }
                            onChange={(opt) => {
                              const v = opt ? String(opt.value) : '';
                              setCodTrib(v);
                              if (v && !codTribOptions.some((o) => String(o.value) === v)) {
                                setCodTribOptions((prev) => [...prev, { value: v, label: v }]);
                              }
                            }}
                            styles={{ container: (base) => ({ ...base, fontSize: 12 }) }}
                          />
                        </div>
                      </label>
                    </div>

                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      <label style={{ fontSize: 12, color: '#6c757d', flex: 1, minWidth: 140, marginBottom: 0 }}>
                        Estado (UF)
                        <div style={{ marginTop: 4 }}>
                          <SearchableSelect
                            options={ufOptions}
                            isLoading={optionsLoading}
                            isClearable
                            placeholder="Selecione o estado..."
                            value={selectedUf}
                            onChange={(opt) => {
                              setUfIBGECod(opt?.uf_IBGECod ?? null);
                              setMunId(null);
                            }}
                            styles={{ container: (base) => ({ ...base, fontSize: 12 }) }}
                          />
                        </div>
                      </label>
                      <label style={{ fontSize: 12, color: '#6c757d', flex: 1, minWidth: 180, marginBottom: 0 }}>
                        Município
                        <div style={{ marginTop: 4 }}>
                          <SearchableSelect
                            options={munOptionsFiltered}
                            isLoading={optionsLoading}
                            isClearable
                            isDisabled={ufIBGECod == null || ufIBGECod === ''}
                            placeholder={
                              ufIBGECod == null || ufIBGECod === ''
                                ? 'Selecione o estado primeiro...'
                                : 'Selecione o município...'
                            }
                            value={munOptionsFiltered.find((o) => Number(o.value) === Number(munId)) || null}
                            onChange={(opt) => setMunId(opt?.value ?? null)}
                            styles={{ container: (base) => ({ ...base, fontSize: 12 }) }}
                          />
                        </div>
                      </label>
                    </div>

                    {emitMissing.length > 0 && kaminoStatus === 'nao_enviado' && (
                      <div style={{ fontSize: 12, color: '#dc3545' }}>
                        Pendências: {emitMissing.join('; ')}.
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={savingNfFields || emitting || loadingModelo}
                        onClick={() => {
                          handleSaveNfFields().catch(() => {});
                        }}
                      >
                        {savingNfFields ? 'Salvando...' : 'Salvar dados da NF'}
                      </Button>
                      {kaminoStatus === 'nao_enviado' && (
                        <Button
                          size="small"
                          variant="contained"
                          disabled={!canClickEmit}
                          onClick={() => {
                            handleEmitKamino().catch(() => {});
                          }}
                        >
                          {emitting ? 'Enviando...' : 'Enviar para emissão'}
                        </Button>
                      )}
                      {(savingNfFields || emitting || optionsLoading || loadingModelo) && <Spinner size="sm" />}
                    </div>
                  </>
                )}
              </div>

              {docsError && <div style={{ color: '#dc3545' }}>{docsError}</div>}

              <div style={{ fontSize: 12, fontWeight: 700, color: '#6c757d' }}>Anexar XML/PDF manualmente</div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 48, color: '#6c757d', fontSize: 12 }}>XML</div>
                  {docsLoading ? (
                    <Spinner size="sm" />
                  ) : xmlDoc?.arquivo_caminho ? (
                    <IconButton size="small" title="Abrir XML" onClick={() => openDoc(xmlDoc)}>
                      <i className="ri-folder-2-line font-size-18" />
                    </IconButton>
                  ) : uploadingXml ? (
                    <Spinner size="sm" />
                  ) : (
                    <Dropzone multiple={false} maxFiles={1} accept={['.xml']} onDrop={handleUploadXml}>
                      {({ getRootProps, getInputProps, isDragActive, isDragReject }) => (
                        <div
                          {...getRootProps()}
                          style={{
                            ...compactDropStyle,
                            borderColor: isDragReject ? 'red' : isDragActive ? '#4aa3ff' : '#ccc',
                            color: isDragReject ? 'red' : isDragActive ? '#4aa3ff' : '#666',
                          }}
                        >
                          <input {...getInputProps()} />
                          XML
                        </div>
                      )}
                    </Dropzone>
                  )}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 48, color: '#6c757d', fontSize: 12 }}>PDF</div>
                  {!nfId || !xmlDoc?.arquivo_caminho ? (
                    <div style={{ fontSize: 12, color: '#6c757d', maxWidth: 220 }}>Anexe o XML antes do PDF.</div>
                  ) : docsLoading ? (
                    <Spinner size="sm" />
                  ) : pdfDoc?.arquivo_caminho ? (
                    <IconButton size="small" title="Abrir PDF (DANFE)" onClick={() => openDoc(pdfDoc)}>
                      <i className="ri-folder-2-line font-size-18" />
                    </IconButton>
                  ) : uploadingPdf ? (
                    <Spinner size="sm" />
                  ) : (
                    <Dropzone multiple={false} maxFiles={1} accept={['.pdf']} onDrop={handleUploadPdf}>
                      {({ getRootProps, getInputProps, isDragActive, isDragReject }) => (
                        <div
                          {...getRootProps()}
                          style={{
                            ...compactDropStyle,
                            borderColor: isDragReject ? 'red' : isDragActive ? '#4aa3ff' : '#ccc',
                            color: isDragReject ? 'red' : isDragActive ? '#4aa3ff' : '#666',
                          }}
                        >
                          <input {...getInputProps()} />
                          PDF
                        </div>
                      )}
                    </Dropzone>
                  )}
                </div>
              </div>
            </div>

            <Dialog open={nfModalOpen} onClose={closeNfModal} fullWidth maxWidth="md">
              <DialogTitle>{nf ? `Nota Fiscal ${nf.nf_num || ''} (ID: ${nf.nf_id})` : `Nota Fiscal (nf_id: ${rec?.nf_id})`}</DialogTitle>
              <DialogContent dividers>
                {nfLoading && (
                  <Box display="flex" alignItems="center" justifyContent="center" py={3}>
                    <Spinner />
                  </Box>
                )}
                {!nfLoading && nfError && (
                  <Typography color="error" variant="body2">
                    {nfError}
                  </Typography>
                )}
                {!nfLoading && !nfError && nf && (
                  <Box display="flex" flexDirection="column" gap={2}>
                    <Box display="flex" gap={2} flexWrap="wrap">
                      <LabelValue label="ID" value={nf.nf_id} />
                      <LabelValue label="Número" value={nf.nf_num || '-'} />
                      <LabelValue label="Status" value={nf.nf_status || '-'} />
                    </Box>
                    <Box display="flex" gap={2} flexWrap="wrap">
                      <LabelValue label="Emissão" value={formatDateTime(nf.nf_dtEmissao)} />
                      <LabelValue label="Competência" value={formatDateOnly(nf.nf_comp)} />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Descrição
                      </Typography>
                      <Box sx={{ border: '1px solid rgba(0,0,0,0.12)', borderRadius: 1, padding: 1.5, whiteSpace: 'pre-wrap', fontSize: 13, maxHeight: 240, overflow: 'auto' }}>
                        {nf.nf_desc || '-'}
                      </Box>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Valores
                      </Typography>
                      <Box display="flex" gap={2} flexWrap="wrap">
                        <LabelValue label="Serviços" value={formatMoney(nf.nf_vlrServicos)} />
                        <LabelValue label="Base" value={formatMoney(nf.nf_baseCalculo)} />
                        <LabelValue label="Líquido" value={formatMoney(nf.nf_vlrLiq)} />
                      </Box>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Tributos (R$)
                      </Typography>
                      <Box display="flex" gap={2} flexWrap="wrap">
                        <LabelValue label="ISS" value={formatMoney(nf.nf_vlrISS)} />
                        <LabelValue label="ISS Ret." value={formatMoney(nf.nf_retISS)} />
                        <LabelValue label="Alíquota" value={formatPercent(nf.nf_aliq)} />
                      </Box>
                    </Box>
                  </Box>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={closeNfModal}>Fechar</Button>
              </DialogActions>
            </Dialog>
          </div>
        }
      />
    </div>
  );
};

export default function FaturamentoDashboard({
  recs,
  mesRef,
  currentView,
  setCurrentView,
  selectedRecId,
  setSelectedRecId,
  onNeedReload,
}) {
  const [dataIsLoading, setDataIsLoading] = useState(false);
  const [itemsInTable, setItemsInTable] = useState([]);
  const [title, setTitle] = useState('Faturamentos da semana');
  const [showBackButton, setShowBackButton] = useState(false);

  const [showDetails, setShowDetails] = useState(false);
  const [selectedRec, setSelectedRec] = useState(null);

  const today = useMemo(() => startOfToday(), []);
  const { start: startMonth, endExclusive: endMonthExclusive } = useMemo(() => monthRefToRange(mesRef), [mesRef]);

  const currentViewRef = useRef(currentView);
  const selectedRecIdRef = useRef(selectedRecId);
  useEffect(() => {
    currentViewRef.current = currentView;
  }, [currentView]);
  useEffect(() => {
    selectedRecIdRef.current = selectedRecId;
  }, [selectedRecId]);

  const { fatLiberados, fatBloqueadosMes, bloqHojeMes, bloqProx7Mes, fatDia, fatMes, fatMesAtraso, fatMesFaturados } = useMemo(() => {
    const withDtPrev = (recs || []).map((r) => ({ r, dtPrev: parseDate(r.rec_dtNfPrev) })).filter((x) => x.dtPrev);

    const liberados = withDtPrev.filter((x) => isLiberado(x.r) && !isFaturado(x.r)).map((x) => x.r);
    const dia = withDtPrev.filter((x) => x.dtPrev.getTime() === today.getTime()).map((x) => x.r);

    const mes = withDtPrev.filter((x) => inRange(x.dtPrev, startMonth, endMonthExclusive)).map((x) => x.r);
    const mesAtraso = withDtPrev
      .filter((x) => inRange(x.dtPrev, startMonth, endMonthExclusive) && x.dtPrev < today && !isFaturado(x.r))
      .map((x) => x.r);

    const mesFaturados = withDtPrev
      .filter((x) => inRange(x.dtPrev, startMonth, endMonthExclusive) && isFaturado(x.r))
      .map((x) => x.r);

    const onlyMonth = (x) => inRange(x.dtPrev, startMonth, endMonthExclusive);
    const bloqueadosMes = withDtPrev
      .filter((x) => onlyMonth(x) && !isLiberado(x.r) && !isFaturado(x.r))
      .map((x) => x.r);

    const bloqHoje = withDtPrev
      .filter((x) => onlyMonth(x) && !isLiberado(x.r) && !isFaturado(x.r) && x.dtPrev.getTime() === today.getTime())
      .map((x) => x.r);

    const prox7End = new Date(today);
    prox7End.setDate(today.getDate() + 7);
    const prox7EndExclusive = new Date(prox7End);
    prox7EndExclusive.setDate(prox7End.getDate() + 1);
    const bloqProx7 = withDtPrev
      .filter((x) => {
        if (!onlyMonth(x)) return false;
        if (isLiberado(x.r) || isFaturado(x.r)) return false;
        return x.dtPrev > today && x.dtPrev < prox7EndExclusive;
      })
      .map((x) => x.r);

    return {
      fatLiberados: liberados,
      fatBloqueadosMes: bloqueadosMes,
      bloqHojeMes: bloqHoje,
      bloqProx7Mes: bloqProx7,
      fatDia: dia,
      fatMes: mes,
      fatMesAtraso: mesAtraso,
      fatMesFaturados: mesFaturados,
    };
  }, [recs, today, startMonth, endMonthExclusive]);

  // mantemos fatDia para outras lógicas/uso futuro; bloqueados do card são restritos ao mês (fatBloqueadosMes)

  useEffect(() => {
    setDataIsLoading(true);
    try {
      const viewMap = {
        liberados: fatLiberados,
        bloqueados: fatBloqueadosMes,
        mesAtraso: fatMesAtraso,
        mes: fatMes,
        mes_faturados: fatMesFaturados,
      };
      const titleMap = {
        liberados: 'Faturamentos liberados',
        bloqueados: 'Faturamentos bloqueados',
        mesAtraso: 'Faturamentos do mês em atraso',
        mes: 'Faturamentos do mês',
        mes_faturados: 'Faturamentos realizados no mês',
      };
      const viewKey = currentViewRef.current;
      const nextView = viewMap[viewKey] ? viewKey : 'mes';
      setItemsInTable(viewMap[nextView] || fatMes);
      setTitle(titleMap[nextView] || 'Faturamentos do mês');
      setShowBackButton(nextView !== 'mes');

      const selId = selectedRecIdRef.current;
      if (selId != null) {
        const updated = (recs || []).find((r) => Number(r?.rec_id) === Number(selId));
        if (updated) {
          setSelectedRec(updated);
          setShowDetails(true);
        }
      }
    } finally {
      setDataIsLoading(false);
    }
  }, [recs, fatLiberados, fatBloqueadosMes, fatDia, fatMes, fatMesAtraso, fatMesFaturados]);

  const columns = useMemo(() => {
    return [
      { accessorKey: 'rec_ep', header: 'EP', size: 200 },
      { accessorKey: 'recProjeto', header: 'Projeto', size: 220 },
      {
        id: 'situacao',
        header: 'Situação',
        accessorFn: (row) => getSituacao(row, today),
        Cell: ({ row }) => <SituacaoPill value={getSituacao(row.original, today)} />,
        size: 120,
      },
      {
        accessorKey: 'rec_dtNfPrev',
        header: 'Limite faturamento',
        sortingFn: 'datetime',
        accessorFn: (row) => parseDate(row.rec_dtNfPrev),
        Cell: ({ row }) => fmtDateBr(row.original.rec_dtNfPrev),
        size: 140,
      },
      {
        id: 'diasParaLimite',
        header: 'Dias p/ limite',
        accessorFn: (row) => {
          const dtPrev = parseDate(row.rec_dtNfPrev);
          if (!dtPrev) return null;
          return diffDays(dtPrev, today);
        },
        Cell: ({ row }) => {
          const dtPrev = parseDate(row.original.rec_dtNfPrev);
          if (!dtPrev) return '-';
          const d = diffDays(dtPrev, today);
          const color = d < 0 && !isFaturado(row.original) ? '#ff5757' : d === 0 ? '#4e515e' : '#6c757d';
          return <span style={{ fontWeight: 800, color }}>{d}</span>;
        },
        size: 110,
      },
      { accessorKey: 'rec_vlrBru', header: 'Valor bruto', Cell: ({ row }) => currencyFormatter.format(num(row.original.rec_vlrBru)), size: 120 },
      { accessorKey: 'rec_vlrLiq', header: 'Valor líquido', Cell: ({ row }) => currencyFormatter.format(num(row.original.rec_vlrLiq)), size: 120 },
      { accessorKey: 'rec_aut', header: 'Liberado', Cell: ({ row }) => <YesNoDot value={isLiberado(row.original)} />, size: 80 },
      { accessorKey: 'rec_fat', header: 'Faturado', Cell: ({ row }) => <YesNoDot value={isFaturado(row.original)} trueColor="#5664d2" falseColor="#ff5757" />, size: 80 },
      {
        id: 'kamino',
        header: 'Kamino',
        accessorFn: (row) => getKaminoEmitStatus(row),
        Cell: ({ row }) => <KaminoStatusPill status={getKaminoEmitStatus(row.original)} />,
        size: 140,
      },
      { accessorKey: 'rec_risco', header: 'Risco', size: 80 },
      { accessorKey: 'rec_id', header: 'ID', size: 80 },
    ];
  }, [today]);

  if (dataIsLoading) {
    return (
      <div className="centered">
        <Spinner />
      </div>
    );
  }

  return (
    <Container fluid>
      <h3 style={{ marginBottom: 16 }}>Faturamento</h3>

      <div className="d-flex justify-content-between align-items-center">
        <MetricCard
          title="Faturamentos liberados"
          recs={fatLiberados}
          className="mr-4"
          selected={currentView === 'liberados'}
          onClick={() => {
            setCurrentView('liberados');
            setItemsInTable(fatLiberados);
            setTitle('Faturamentos liberados');
            setShowBackButton(true);
          }}
        />
        <MetricCard
          title="Faturamentos bloqueados"
          recs={fatBloqueadosMes}
          className="mr-4"
          selected={currentView === 'bloqueados'}
          meta={
            <div className="d-flex flex-column" style={{ gap: 4, marginBottom: 8, fontSize: 12, color: '#6c757d' }}>
              <div className="d-flex justify-content-between" style={{ gap: 10 }}>
                <span>Hoje</span>
                <span style={{ fontWeight: 900, color: bloqHojeMes.length > 0 ? '#b46900' : '#6c757d' }}>
                  {bloqHojeMes.length}
                </span>
              </div>
              <div className="d-flex justify-content-between" style={{ gap: 10 }}>
                <span>Próximos 7 dias</span>
                <span style={{ fontWeight: 900, color: bloqProx7Mes.length > 0 ? '#b46900' : '#6c757d' }}>
                  {bloqProx7Mes.length}
                </span>
              </div>
            </div>
          }
          onClick={() => {
            setCurrentView('bloqueados');
            setItemsInTable(fatBloqueadosMes);
            setTitle('Faturamentos bloqueados');
            setShowBackButton(true);
          }}
        />
        <MetricCard
          title="Faturamentos do mês em atraso"
          recs={fatMesAtraso}
          className="mr-4"
          selected={currentView === 'mesAtraso'}
          onClick={() => {
            setCurrentView('mesAtraso');
            setItemsInTable(fatMesAtraso);
            setTitle('Faturamentos do mês em atraso');
            setShowBackButton(true);
          }}
        />
        <MetricCard
          title="Faturamentos do mês"
          recs={fatMes}
          selected={currentView === 'mes'}
          onClick={() => {
            setCurrentView('mes');
            setItemsInTable(fatMes);
            setTitle('Faturamentos do mês');
            setShowBackButton(true);
          }}
        />
      </div>

      <div className="d-flex mt-3">
        <div className="d-flex flex-column" style={{ width: '50%' }}>
          <SimpleCard
            standard={false}
            title={
              <div className="d-flex justify-content-between align-items-center">
                <h4 className="text-truncate font-size-14 mb-2">{title}</h4>
                {showBackButton && (
                  <IconButton
                    onClick={() => {
                      setCurrentView('mes');
                      setItemsInTable(fatMes);
                      setTitle('Faturamentos do mês');
                      setShowBackButton(false);
                    }}
                  >
                    <ArrowBack />
                  </IconButton>
                )}
              </div>
            }
            text={
              <MaterialReactTable
                columns={columns}
                data={itemsInTable}
                initialState={{
                  density: 'compact',
                  pagination: { pageIndex: 0, pageSize: 10 },
                  sorting: [
                    { id: 'diasParaLimite', desc: false },
                    { id: 'rec_vlrBru', desc: true },
                  ],
                }}
                enableColumnResizing
                enableGrouping={false}
                enableGlobalFilter
                enableFilters
                enablePagination={itemsInTable.length > 10}
                muiTableBodyCellProps={{ sx: { fontSize: '10px' } }}
                muiTablePaginationProps={{
                  sx: {
                    '.MuiTablePagination-displayedRows': { fontSize: '12px' },
                    marginTop: '0px',
                    fontSize: '12px',
                  },
                  labelRowsPerPage: <span style={{ fontSize: '12px' }}>Itens por página</span>,
                  rowsPerPageOptions: [10, 50, 100],
                }}
                positionPagination="bottom"
                enableDensityToggle={false}
                enableColumnFilters
                muiTableHeadCellProps={{
                  sx: {
                    '.MuiSvgIcon-root': { fontSize: '18px' },
                    fontSize: '12px',
                    fontWeight: 'normal',
                    padding: '8px',
                  },
                }}
                muiTableBodyRowProps={({ row }) => ({
                  onClick: () => {
                    setShowDetails(true);
                    setSelectedRec(row.original);
                    setSelectedRecId(row.original?.rec_id ?? null);
                  },
                  sx: (() => {
                    const r = row.original;
                    const dtPrev = parseDate(r?.rec_dtNfPrev);
                    const dias = dtPrev ? diffDays(dtPrev, today) : null;
                    const fat = isFaturado(r);
                    const lib = isLiberado(r);
                    let bg = undefined;
                    if (!fat && dias != null && dias < 0) bg = 'rgba(255,87,87,0.12)';
                    else if (!fat && lib) bg = 'rgba(28,187,140,0.10)';
                    else if (!fat) bg = 'rgba(78,81,94,0.06)';
                    return { cursor: 'pointer', backgroundColor: bg };
                  })(),
                })}
              />
            }
          />
        </div>

        <div className="d-flex flex-column ml-2 mr-4" style={{ width: '50%' }}>
          {showDetails && (
            <DetailsCard
              rec={selectedRec}
              onRecPatched={(patch) => setSelectedRec((prev) => (prev ? { ...prev, ...patch } : prev))}
              onNeedReload={onNeedReload}
            />
          )}
        </div>
      </div>
    </Container>
  );
}

